import React, { forwardRef } from 'react'
import styled from 'styled-components'
import { FormattedMessage } from 'react-intl'
import { Link } from 'react-router'
import { Link as RebassLink } from 'rebass/styled-components'

import {
  Menu,
  MenuTrigger,
  MenuContent,
  MenuSection,
  MenuWrapper,
  MenuContainer,
  MenuHeading,
  MenuPopover,
  MenuAction
} from '../Menu'
import { CheckIcon, SwitcherIcon } from '../Icon'
import { Flex, Box } from '../Grid'
import LoadingLine from '../Loading/Line'
import Text from '../Type'

export const MenuLink = forwardRef(
  (
    { active, children, to, forceRefresh, hasUnread = false, isUnread = false, variant = 'menu', sx, ...props },
    ref
  ) => {
    if (forceRefresh) {
      props.as = 'a'
      props.href = to
    } else {
      props.as = Link
      props.to = to
    }

    return (
      <RebassLink
        data-testid={isUnread ? 'unread-menu-item' : 'menu-item'}
        ref={ref}
        variant={`links.${variant}`}
        sx={{
          display: 'block',
          lineHeight: 1.1,
          // Add padding if any of the menu items have not been read
          ...(hasUnread ? { pl: '16px' } : null),
          // If an individual link has not been read, add a badge via pseudo element
          ...(isUnread
            ? {
                position: 'relative',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: '18px',
                  left: '0',
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: 'green200'
                }
              }
            : null),
          ...sx
        }}
        {...props}
        className={active ? 'active' : null}
      >
        <Flex justifyContent="space-between">
          <Box>{children}</Box>
          <Box>{active ? <CheckIcon verticalAlign="middle" ml="8px" mt="-2px" /> : null}</Box>
        </Flex>
      </RebassLink>
    )
  }
)
MenuLink.displayName = 'MenuLink'

export const MenuInput = styled(RebassLink).attrs(props => ({
  forwardedAs: 'button',
  ...props
}))`
  border: 1px solid transparent;
  background: none;
  display: block;
  outline: 0;
  padding: 0;
  width: 100%;

  &[data-selected] {
    input {
      border-color: ${({ theme }) => theme.colors.blue200};
    }
  }
`

export const SwitchTrigger = props => (
  <MenuTrigger
    data-name="SwitchTrigger"
    variant="links.navPrimary"
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    }}
    {...props}
  >
    {props.children}
    <SwitcherIcon aria-hidden color="light-dark(var(--brand-blue-100), var(--brand-blue-200))" />
  </MenuTrigger>
)

export const MenuLinks = ({ title, links, loading, maxHeight, hasMore, hasUnread = false, onLoadMore }) => {
  return (
    <MenuWrapper>
      <MenuContainer maxHeight={maxHeight}>
        {title ? <MenuHeading>{title}</MenuHeading> : null}
        {loading ? (
          <>
            <Box ml={2} mt={2}>
              <LoadingLine backgroundColor="light-dark(var(--brand-grey-50), var(--brand-grey-500))" />
            </Box>
          </>
        ) : null}
        {loading
          ? null
          : links
              .sort((a, b) => b.active - a.active)
              .map(({ name, description, to, forceRefresh, isUnread = false, ...props }) => (
                <MenuLink
                  key={to}
                  to={to}
                  forceRefresh={forceRefresh}
                  hasUnread={hasUnread}
                  isUnread={isUnread}
                  {...props}
                >
                  {name}
                  {description ? (
                    <Text level="xs" color="grey300">
                      <br />
                      {description}
                    </Text>
                  ) : null}
                </MenuLink>
              ))}
        {loading ? null : hasMore ? (
          <MenuAction
            forwardedAs="button"
            onClick={e => {
              e.preventDefault()
              onLoadMore()
            }}
            my="10px"
            mx="auto"
            width={1}
          >
            <FormattedMessage id="menu.load_more" />
          </MenuAction>
        ) : null}
      </MenuContainer>
    </MenuWrapper>
  )
}

export { Menu, MenuPopover, MenuTrigger, MenuContent, MenuSection, MenuAction }
